import { useState } from 'react';
import { Loader2, Printer } from 'lucide-react';
import { getAllServiceProspects } from '@generated/server/services';
import { getStaffPass } from '@generated/hooks/staffPass';
import { RHSR_LOGO, VSR_LOGO } from '@generated/components/AdGuide';
import { ORDER, category } from '@generated/components/ServicesPage';

type Row = Awaited<ReturnType<typeof getAllServiceProspects>>['items'][number];
type Node = Record<string, unknown> | string;

async function dataUrl(src: string): Promise<string | null> {
  try {
    const blob = await (await fetch(src)).blob();
    return await new Promise((ok) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => ok(null); r.readAsDataURL(blob); });
  } catch (e) { console.warn('logo not loaded', e); return null; }
}

const box = (label: string): Node => ({ columns: [{ width: 12, canvas: [{ type: 'rect', x: 0, y: 1, w: 8, h: 8, lineWidth: 0.8 }] }, { text: label, width: 'auto' }], columnGap: 2, width: 'auto' });
const line = (w: number) => ({ canvas: [{ type: 'line', x1: 0, y1: 18, x2: w, y2: 18, lineWidth: 0.5, lineColor: '#888888' }] });

const blank = (w: number) => ({ width: w, canvas: [{ type: 'line', x1: 0, y1: 10, x2: w, y2: 10, lineWidth: 0.6, lineColor: '#555555' }] });
/** "Call 1" / "Call 2" drawn in Alfa Slab One (loaded from Google Fonts) and embedded as images. */
async function callLabels(): Promise<Record<number, string | null>> {
  try {
    {
      const l = document.createElement('link'); l.rel = 'stylesheet'; l.crossOrigin = 'anonymous'; l.dataset.alfa = '1';
      l.href = 'https://fonts.googleapis.com/css2?family=Alfa+Slab+One&display=swap'; document.head.appendChild(l);
      await new Promise((ok) => { l.onload = ok; l.onerror = ok; setTimeout(ok, 3000); });
    }
    await document.fonts.load('48px "Alfa Slab One"');
    const draw = (t: string) => {
      const c = document.createElement('canvas'); const x = c.getContext('2d'); if (!x) return null;
      x.font = '48px "Alfa Slab One"'; c.width = Math.ceil(x.measureText(t).width) + 4; c.height = 60;
      x.font = '48px "Alfa Slab One"'; x.fillStyle = '#1E1E1E'; x.textBaseline = 'middle'; x.fillText(t, 2, 32);
      return c.toDataURL('image/png');
    };
    const out = { 1: draw('Call 1'), 2: draw('Call 2') };
    document.querySelector('link[data-alfa]')?.remove(); // keep page-image exports (flyer) from tripping on this stylesheet
    return out;
  } catch (e) { console.warn('Alfa Slab One not loaded', e); return { 1: null, 2: null }; }
}
let LABELS: Record<number, string | null> = { 1: null, 2: null };
const callLabel = (n: number): Node => (LABELS[n] ? { image: LABELS[n] as string, fit: [48, 13], width: 48 } : { text: `Call ${n}`, width: 'auto', bold: true });

function callRow(n: number): Node {
  return {
    margin: [0, 6, 0, 0], stack: [
      { columns: [callLabel(n), { text: 'Date:', width: 'auto' }, blank(85), box('No answer'), box('Left message'), box('Spoke with:'), blank(140)], columnGap: 8 },
      { margin: [0, 5, 0, 0], columns: [{ text: 'How it went:', width: 'auto', bold: true }, box('Interested'), box('Call back'), box('Send info'), box('Not interested')], columnGap: 10 },
      { margin: [0, 3, 0, 0], text: 'Notes:' }, line(524), line(524),
    ],
  };
}

function block(i: Row): Node {
  const em = i.email?.email?.trim();
  return {
    unbreakable: true, margin: [0, 0, 0, 10],
    table: { widths: ['*'], body: [[{
      margin: [6, 5, 6, 6], stack: [
        { columns: [{ text: i.name, bold: true, fontSize: 12, color: '#1F4FB8' }, { text: i.phone || 'No phone on file', bold: true, fontSize: 12, color: '#C8102E', alignment: 'right', width: 'auto' }] },
        ...(i.contactName ? [{ text: i.contactName, fontSize: 9 }] : []),
        em ? { margin: [0, 2, 0, 1], text: [{ text: 'Email: ', bold: true }, em], fontSize: 10 } : { margin: [0, 2, 0, 2], columns: [{ text: 'Enter email:', bold: true, fontSize: 10, width: 'auto' }, blank(260)], columnGap: 6 },
        ...(i.address ? [{ text: i.address, fontSize: 9, color: '#555555' }] : []),
        callRow(1), callRow(2),
      ],
    }]] },
    layout: { hLineWidth: () => 1, vLineWidth: () => 1, hLineColor: () => '#222222', vLineColor: () => '#222222' },
  };
}

/** Printable phone call worksheet for one category of Neighborhood Services prospects. */
export function CallSheet() {
  const [cat, setCat] = useState('Movers');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function make() {
    setBusy(true); setErr('');
    try {
      const res = await getAllServiceProspects({ data: { pass: getStaffPass() } });
      const rows = (res?.items ?? []).filter((i) => category(i) === cat && i.outreachStatus !== 'Booked Ad' && i.outreachStatus !== 'Not Interested' && i.outreachStatus !== 'Bad Contact');
      if (!rows.length) { setErr(`No ${cat} prospects left to call.`); return; }
      const [a, b, lb] = await Promise.all([dataUrl(RHSR_LOGO), dataUrl(VSR_LOGO), callLabels()]); LABELS = lb;
      const logos = [a, b].filter((x): x is string => !!x).map((image) => ({ image, fit: [44, 44] as [number, number], width: 50 }));
      const today = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'long', day: 'numeric', year: 'numeric' });
      const pdfMake = (await import('pdfmake/build/pdfmake')).default as unknown as { vfs?: unknown; createPdf: (d: unknown) => { download: (n?: string) => void } };
      if (!pdfMake.vfs) { const f = (await import('pdfmake/build/vfs_fonts')).default as unknown as { pdfMake?: { vfs?: unknown }; vfs?: unknown }; pdfMake.vfs = f?.pdfMake?.vfs ?? f?.vfs; }
      pdfMake.createPdf({
        pageSize: 'LETTER', pageMargins: [36, 36, 36, 40], defaultStyle: { fontSize: 9 },
        footer: (p: number, n: number) => ({ text: `${cat} call sheet  ·  page ${p} of ${n}`, alignment: 'center', fontSize: 8, color: '#777777' }),
        content: [
          { columns: [...logos, { stack: [{ text: `Neighborhood Services Call Sheet: ${cat}`, fontSize: 16, bold: true }, { text: `Caller: Kitty  ·  Printed ${today}  ·  ${rows.length} businesses`, fontSize: 9, color: '#555555' }], margin: [6, 6, 0, 0] }], columnGap: 4, margin: [0, 0, 0, 6] },
          { text: 'Pitch: one-inch full-color ad, runs in both papers. $100 for one month, $500 for six months, $850 for a year.', italics: true, margin: [0, 0, 0, 10] },
          ...rows.map(block),
        ],
        info: { title: `${cat} call sheet` },
      }).download(`${cat} call sheet ${new Date().toLocaleString('en-CA', { timeZone: 'America/New_York', hour12: false }).replace(/[^0-9]+/g, '-').slice(0, 16)}.pdf`);
    } catch (e) { console.error(e); setErr('Could not make the call sheet. Please try again.'); } finally { setBusy(false); }
  }

  return (
    <div className="max-w-xl space-y-4">
      <p className="text-sm text-muted-foreground">A printable sheet for phone calls. Each business gets room for two calls: the date, whether someone answered or you left a message, who you spoke with, how it went, and notes. Businesses already booked, not interested or with bad contact info are left off.</p>
      <label className="block text-sm font-semibold">Category
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="mt-1 min-h-11 w-full border-2 border-foreground bg-card px-2 text-sm">{ORDER.map((c) => <option key={c}>{c}</option>)}</select>
      </label>
      <button type="button" onClick={make} disabled={busy} className="inline-flex min-h-11 items-center gap-2 border-2 border-foreground bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}Download call sheet (PDF)</button>
      {err && <p className="text-sm text-destructive">{err}</p>}
    </div>
  );
}
